/**
 * Commentary driver (plan P2c): same-origin opus decoded with decodeAudioData and played on its own bus.
 * Presentation time drives it, same rule as the radio element: inside [t_pres, t_pres + dur) play from
 * the offset, re-seek on > DRIFT_S drift, else stop. In free mode the line's t_pres is mapped back to
 * race time (`raceOfPres`) and its presentation position is (raceT - t_race) / rate; above 1.5x a line
 * starts only if it is still within START_WINDOW_S of its race time (then the latch lets it finish),
 * otherwise only its caption shows. Lines are fetched and decoded PREFETCH ahead.
 */
import { DRIFT_S, VOICE_RATE_MAX, commentaryTarget } from './policy.ts';
import type { AudioInput, CommentaryFile, CommentaryLine } from './types.ts';

const PREFETCH = 2;
const START_WINDOW_S = 3;
/** H3: a decoded line is dropped once its end is this far behind the playhead (a seek back
 *  re-fetches and re-decodes); decoded commentary is 40–67 MB per take and never evicted otherwise. */
const EVICT_S = 30;

export interface SourceLike {
  buffer: unknown;
  connect(dest: unknown): unknown;
  start(when: number, offset: number): void;
  stop(): void;
  onended: (() => void) | null;
}
export interface CommentaryCtxLike {
  readonly currentTime: number;
  decodeAudioData(data: ArrayBuffer): Promise<unknown>;
  createBufferSource(): SourceLike;
}

export class CommentaryDriver {
  private lines: CommentaryLine[] = [];
  private baseUrl = '';
  private raceOfPres: (t: number) => number = (t) => t;
  private readonly buffers = new Map<string, Promise<unknown>>();
  private src: SourceLike | null = null;
  private srcLine: CommentaryLine | null = null;
  private srcStart = 0;      // ctx time the current source's position 0 is at
  private latched = new Set<string>();
  readonly unavailable = new Set<string>();
  voiceUnavailable = false;

  constructor(
    private readonly ctx: CommentaryCtxLike,
    private readonly bus: unknown,
    private readonly fetchBytes: (url: string) => Promise<ArrayBuffer> = async (u) => (await fetch(u)).arrayBuffer(),
  ) {}

  setFile(file: CommentaryFile | null, baseUrl: string, raceOfPres?: (t: number) => number): void {
    this.stop();
    this.buffers.clear(); this.unavailable.clear(); this.latched.clear();
    this.voiceUnavailable = false;
    this.lines = file ? [...file.lines].sort((a, b) => a.t_pres - b.t_pres) : [];
    this.baseUrl = baseUrl.endsWith('/') || baseUrl === '' ? baseUrl : `${baseUrl}/`;
    if (raceOfPres) this.raceOfPres = raceOfPres;
  }

  private load(l: CommentaryLine): Promise<unknown> {
    let p = this.buffers.get(l.id);
    if (!p) {
      p = this.fetchBytes(this.baseUrl + l.audio).then((b) => this.ctx.decodeAudioData(b));
      p.catch(() => { this.unavailable.add(l.id); this.voiceUnavailable = true; });
      this.buffers.set(l.id, p);
    }
    return p;
  }

  /** H3: drop the decoded buffers of lines whose end is more than EVICT_S behind the playhead
   *  (in the input's own time base, so free-mode rates and seeks are covered). A line that could
   *  still play or be prefetched is never behind by that much, so nothing schedulable is lost. */
  private evictBehind(inp: AudioInput): void {
    if (this.buffers.size === 0) return;
    for (const l of this.lines) {
      if (this.presOf(l, inp) - (l.t_pres + l.dur) > EVICT_S) this.buffers.delete(l.id);
    }
  }

  private stop(): void {
    const s = this.src;
    this.src = null; this.srcLine = null;
    if (s) { s.onended = null; try { s.stop(); } catch { /* already stopped */ } }
  }

  /** presentation position of the line for this input (free mode: from race time), may be outside the line */
  private presOf(l: CommentaryLine, inp: AudioInput): number {
    if (inp.mode === 'director') return inp.presT;
    return l.t_pres + (inp.raceT - this.raceOfPres(l.t_pres)) / Math.max(inp.effectiveRate, 1e-9);
  }

  /** once per frame; `allowed` is the rate policy's commentary flag. Returns the line being played, or null. */
  update(inp: AudioInput, allowed: boolean): CommentaryLine | null {
    const ahead = this.lines.filter((l) => this.presOf(l, inp) <= l.t_pres + l.dur).slice(0, PREFETCH + 1);
    if (allowed) for (const l of ahead) void this.load(l).catch(() => undefined);
    this.evictBehind(inp);

    let line: CommentaryLine | null = null, pos = 0;
    if (allowed) {
      for (const l of this.lines) {
        const t = commentaryTarget(this.presOf(l, inp), l);
        if (t === null || this.unavailable.has(l.id)) continue;
        if (inp.mode === 'free' && inp.effectiveRate > VOICE_RATE_MAX && !this.latched.has(l.id)) {
          const since = inp.raceT - this.raceOfPres(l.t_pres);
          if (since < 0 || since > START_WINDOW_S) continue;      // caption only
        }
        line = l; pos = t; break;
      }
    }
    if (!line) { this.stop(); this.latched.clear(); return null; }
    this.latched.add(line.id);
    if (this.srcLine === line) {
      if (Math.abs(this.ctx.currentTime - this.srcStart - pos) > DRIFT_S) this.start(line, pos);
    } else {
      this.stop();
      this.start(line, pos);
    }
    return line;
  }

  /** start (or restart at pos) once the buffer is decoded; a line that is not ready yet plays from where it is on arrival */
  private start(line: CommentaryLine, pos: number): void {
    this.stop();
    this.srcLine = line;                      // claimed now so the next frame does not start it twice
    this.srcStart = this.ctx.currentTime - pos;
    const mine = line;
    void this.load(line).then((buf) => {
      if (this.srcLine !== mine || this.src) return;
      const s = this.ctx.createBufferSource();
      s.buffer = buf;
      s.connect(this.bus);
      s.onended = () => { if (this.src === s) { this.src = null; } };
      this.src = s;
      s.start(0, Math.max(0, this.ctx.currentTime - this.srcStart));
    }, () => undefined);
  }

  get playing(): boolean { return this.src !== null; }
  get activeId(): string | null { return this.srcLine?.id ?? null; }
}
